import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {ModalWindowComponent} from "../../../std-components/modal-window/modal-window.component";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {CommonModule} from "@angular/common";
import {FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {
  IGetBuildingRoom,
  ISetBuildingRoomsFloorValues,
  StagingService
} from "../../../new-information/services/staging.service";
import {finalize, forkJoin} from "rxjs";
import {ToastrService} from "ngx-toastr";
import {RoomTypeEnum} from "../../../shared/models/models";
import {NgxMaskDirective, provideNgxMask} from "ngx-mask";

@Component({
  selector: 'app-create-commercial',
  templateUrl: './create-commercial.component.html',
  styleUrl: './create-commercial.component.scss',
  imports: [
    ModalWindowComponent,
    CommonModule,
    ReactiveFormsModule,
    NgxMaskDirective,
  ],
  standalone: true,
  providers: [
    provideNgxMask()
  ]
})
export class CreateCommercialComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<CreateCommercialComponent>);
  readonly data = inject<{ buildingID: number }>(MAT_DIALOG_DATA);

  floorsCount = signal<number | null>(null);
  MIN_FLOORS: number = 0;
  // Создаем сигнал с ошибкой валидации
 floorsCountError = computed(() => {
    const value = this.floorsCount();
    if (value === null) return 'Пожалуйста, укажите количество этажей.';
    if (value < this.MIN_FLOORS) return `Количество этажей не может быть меньше ${this.MIN_FLOORS}.`;
    return null;
  });
  currentStep: StepEnum = StepEnum.FirstStep;
  form!: FormGroup;
  loading = false;
  buildingRooms: IGetBuildingRoom[] = [];
  uniqFloors: number[] = [];
  existsRooms: { floorID: number, placeCount: number }[] = [];

  constructor(private fb: FormBuilder,
              private stagingService: StagingService,
              private toastr: ToastrService) {
    this.form = this.fb.group({
      floors: this.fb.array([])
    });
  }

  ngOnInit() {
    this.loading = true;

    this.stagingService.getBuildingRooms(this.data.buildingID, RoomTypeEnum.Commercial)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (res) => {
          this.buildingRooms = res;
          this.uniqFloors = Array.from(new Set(res.map(item => item.floorID))).sort((a, b) => a - b);
          const totalUniqueFloors = this.uniqFloors.length;
          if (totalUniqueFloors > 0) {
            this.MIN_FLOORS = totalUniqueFloors;
            this.floorsCount.set(totalUniqueFloors);
          }
        },
        error: (err) => {
          console.error('Ошибка при получении данных:', err);
        }
      });
  }

  get floors(): FormArray {
    return this.form.get('floors') as FormArray;
  }

  getFloorControl(index: number): FormControl {
    return this.floors.at(index) as FormControl;
  }

  updateFloors(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    const number = parseInt(value, 10);
    this.floorsCount.set(Number.isNaN(number) ? null : number);
  }

  nextStep() {
    const count = this.floorsCount();
    if (!count || count <= 0) return;
    this.floors.clear();
    this.existsRooms = [];

    if (this.buildingRooms.length > 0) {
      for (let index = 1; index < count + 1; index++) {
        this.floors.push(new FormControl(0, Validators.required));
        const placeCount = this.buildingRooms.filter(item => item.floorID === index).length;
        this.existsRooms.push({floorID: index, placeCount});
      }
    } else if (this.buildingRooms.length === 0) {
      for (let i = 0; i < count; i++) {
        this.floors.push(new FormControl(null, Validators.required));
      }
    }

    this.currentStep = StepEnum.SecondStep;
  }

  prevStep() {
    this.currentStep = StepEnum.FirstStep;
  }

  close() {
    this.dialogRef.close();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched(); // показать ошибки
      return;
    }

    const formArray = this.form.get('floors') as FormArray;

    const dirtyFloorsPlaces:{index: number, value: number}[] = formArray.controls
      .map((control, index) => ({ value: control.value, dirty: control.dirty, index }))
      .filter(control => control.dirty)
      .map(control => ({ index: control.index, value: control.value }));

    const res: ISetBuildingRoomsFloorValues[] = dirtyFloorsPlaces.map((item, index) => {
      return {
        buildingID: this.data.buildingID,
        roomTypeID: RoomTypeEnum.Commercial,
        roomTypeItemID: null,
        floorID: item.index + 1,
        placeCount: item.value,
      } as ISetBuildingRoomsFloorValues;
    })

    const requests = res.map(item => this.stagingService.setBuildingRoomsFloorValues(item));

    forkJoin(requests).subscribe({
      next: _ => {
        this.toastr.success('Успешно созданы коммерческие помещения!');
        this.dialogRef.close(true);
      },
      error: err => {
        console.error('Ошибка при сохранении этажей:', err);
      }
    });
  }


  protected readonly StepEnum = StepEnum;
  protected readonly FormControl = FormControl;
}

enum StepEnum {
  FirstStep = 1,
  SecondStep = 2
}
